export const APP_ROLES = {
    SUPER_ADMIN: 'super-admin',
    ADMIN: 'admin',
    DESTYTOJAS: 'destytojas',
    KURATORIUS: 'kuratorius',
    USER: 'user',
} as const;

export type AppRole = (typeof APP_ROLES)[keyof typeof APP_ROLES];
export type ProfileGroup = string | string[] | null | undefined;

const ROLE_ALIASES: Record<AppRole, string[]> = {
    [APP_ROLES.SUPER_ADMIN]: ['super-admin'],
    [APP_ROLES.ADMIN]: ['admin', 'administratorius'],
    [APP_ROLES.DESTYTOJAS]: ['destytojas', 'dėstytojas', 'lecturer'],
    [APP_ROLES.KURATORIUS]: ['kuratorius', 'curator'],
    [APP_ROLES.USER]: ['user', 'student'],
};

const ROLE_PRIORITY: AppRole[] = [APP_ROLES.SUPER_ADMIN, APP_ROLES.ADMIN, APP_ROLES.DESTYTOJAS, APP_ROLES.KURATORIUS];

export const toGroupList = (group: ProfileGroup): string[] => {
    if (!group) {
        return [];
    }

    return Array.isArray(group) ? group : [group];
};

const normalizeGroupValue = (value: string): string => value.trim().toLowerCase();

export const normalizeRole = (value?: string | null): AppRole | null => {
    if (!value) {
        return null;
    }

    const normalizedValue = normalizeGroupValue(value);

    const matchedRole = (Object.values(APP_ROLES) as AppRole[]).find(role =>
        ROLE_ALIASES[role].some(alias => alias === normalizedValue),
    );

    return matchedRole || null;
};

export const getRolesFromGroup = (group: ProfileGroup): AppRole[] => {
    const roles = new Set<AppRole>();

    toGroupList(group).forEach(value => {
        const role = normalizeRole(value);
        if (role && role !== APP_ROLES.USER) {
            roles.add(role);
        }
    });

    return Array.from(roles);
};

export const getPrimaryRole = (group: ProfileGroup): AppRole => {
    const roles = getRolesFromGroup(group);
    const primaryRole = ROLE_PRIORITY.find(role => roles.includes(role));

    return primaryRole || APP_ROLES.USER;
};

export const hasRole = (group: ProfileGroup, role: AppRole): boolean => {
    if (role === APP_ROLES.USER) {
        return getPrimaryRole(group) === APP_ROLES.USER;
    }

    return getRolesFromGroup(group).includes(role);
};

export const hasAnyRole = (group: ProfileGroup, roles: AppRole[]): boolean => {
    return roles.some(role => hasRole(group, role));
};

export const getManagedGroupIds = (group: ProfileGroup): string[] => {
    return toGroupList(group).filter(value => !normalizeRole(value));
};
